<template>
  <div class="chat-header">
    <label :class="{ active: chatIndicator }" title="Show Chats">
      <input v-model="showChatList" type="checkbox" hidden />
      <i class="bx bxs-message-alt-check" />
    </label>
    <span>CHAT - {{ currentRoom?.name ?? '' }}</span>
    <label :title="allowChatTitle(app.allowChat)">
      <i
        
        class="bx bx-radio-circle-marked"
        :style="app.allowChat ? (app.isTeacher() ? 'cursor: hand; color: green' : 'cursor: default; color: green') : (app.isTeacher() ? 'cursor: hand; color: red' : 'cursor: default; color: red')"
        @click="setChatStatus"
      />
    </label>
    <label :title="chatFunnelTitle(app.funnelChat)" :class="{ chatfunnelactive: app.funnelChat }">
      <i
        v-if="app.isTeacher() || app.funnelChat"
        class="bx bxs-filter-alt"
        :style="app.isTeacher() ? 'cursor: hand' : 'cursor: default'"
        @click="setChatFunneling"
      />
    </label>
    <label :title="app.dockRight ? 'Dock Left' : 'Dock Right'">
      <input v-model="app.dockRight" type="checkbox" hidden />
      <i class="bx" :class="app.dockRight ? 'bxs-arrow-from-right' : 'bxs-arrow-from-left'" />
    </label>

    <ul v-show="showChatList" v-if="app.connection.currentRoom" class="chat-list">
      <li
        :class="{ active: chatIndicators[app.connection.currentRoom.name] && app.connection.currentRoom !== currentRoom }"
        @click="emit('roomSelected', app.connection.currentRoom!.name)"
      >
        <i class="bx bxs-message-alt-check" />
        {{ app.connection.currentRoom.name }}
      </li>
      <li
        v-for="user in usersWithMyMessages"
        :key="'chat-list' + user.id"
        :class="{ active: chatIndicators[user.id] && user !== currentRoom }"
        @click="(emit('roomSelected', user.id), (app.currentPrivateChat = user.id))"
      >
        <i class="bx bxs-message-alt-check" />
        {{ user.name }}
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { getApp } from '../models/App';
import { Room } from '../models/Room';
import { User } from '../models/User';

const emit = defineEmits<{
  (e: 'roomSelected', roomName: string): void;
}>();

const app = getApp();
const showChatList = ref(false);
const chatIndicators = computed(() => app.chatIndicator);
const chatIndicator = computed(() => {
  const obj = Object.assign({}, app.chatIndicator);

  return Object.entries(obj).find(([key, val]) => key !== app.currentChat && val)?.[1] ?? false;
  // ?? (obj[app.currentChat] ? app.currentChat : false)
});

const currentRoom = computed(() => Room.get(app.currentChat) || User.get(app.currentChat));

const usersWithMyMessages = computed(() => {
  return User.all().filter(
    (u) =>
      !!u.messages.filter((m) => m.to === app.getUser()?.id || m.from === app.getUser()?.id).length ||
      app.currentPrivateChat === u.id,
  );
});

const allowChatTitle = (on: boolean) => {
  if (app.isTeacher() && on) {
    return 'Turn Off Chat';
  } else if (app.isTeacher() && !on) {
    return 'Restore Chat';
  } else if (!app.isTeacher() && !on) {
    return 'Chat is off.';
  } else {
    return 'Chat is active.';
  }
};

const chatFunnelTitle = (on: boolean) => {
  if (app.isTeacher() && on) {
    return 'Disable Chat Funneling';
  } else if (app.isTeacher() && !on) {
    return 'Funnel Chat';
  } else {
    return 'Chat is being funneled.';
  }
};

const setChatStatus = () => {
  // Only allow teachers to allow/disallow the chat
  if (!app.isTeacher()) return;

  // Set if the chat is allowed (or not) locally.
  app.allowChat = !app.allowChat;

  // Tell the server the status of the chat.
  app.restrictedSend(`[allowChat]${app.allowChat}`);
};

const setChatFunneling = () => {
  // Only allow teachers to turn on chat funneling
  if (!app.isTeacher()) return;

  // Set the funneling (or not) of the chat locally.
  app.funnelChat = !app.funnelChat;

  // Tell the server the chat is being funneled.
  app.restrictedSend(`[funnelChat]${app.funnelChat}`);
};
</script>

<style scoped>
.chat-header {
  display: flex;
  font-size: 14px;
  height: 26px;
  color: white;
  background: var(--color-gray);
  align-items: center;
  position: relative;
  z-index: 9; /* Sound effects div is z-index: 10.  Setting this to 9 so that the sound effects dropdown lays over the chat. */
}

.chat-header > span {
  flex-grow: 1;
}

.chat-header > i,
.chat-header > label {
  cursor: pointer;
  font-size: 20px;
  margin-right: 3px;
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.chat-header > i.active,
.chat-header > label.active {
  background: var(--color-accent-icon);
  /* color: var(--color-accent-icon); */
}

.chat-header > i.chatfunnelactive,
.chat-header > label.chatfunnelactive {
  color: red;
}

.chat-header > i:last-child,
.chat-header > label:last-child {
  margin: 0;
}

.chat-header > i:first-child,
.chat-header > label:first-child {
  height: 26px;
  width: 26px;
}

.chat-header > ul.chat-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  background: white;
  position: absolute;
  top: 26px;
  left: 0px;
  color: black;
}

.chat-header > ul.chat-list > li {
  display: flex;
  align-items: center;
  font-weight: bold;
  padding-right: 20px;
  cursor: pointer;
}

.chat-header > ul.chat-list > li:hover {
  background: var(--color-accent-room);
}

.chat-header > ul.chat-list > li + li {
  border-top: 1px solid #f6f7f7;
}

.chat-header > ul.chat-list > li > i {
  height: 30px;
  width: 30px;
  text-align: center;
  line-height: 30px;
  visibility: hidden;
  color: var(--color-accent-icon);
}

.chat-header > ul.chat-list > li.active > i {
  visibility: visible;
}
</style>
